/**
 * Shared public-facing layout for non-authed pages (legal docs, pricing,
 * checkout, payment-result, contact). Light Mode + Indigo/Emerald palette
 * to match the in-app workspace.
 */
import { Link, useLocation } from "react-router-dom";
import { Mail, Scale, ArrowRight } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

const NAV = [
    { to: "/pricing",         label: "Pricing" },
    { to: "/legal/terms",     label: "Terms" },
    { to: "/legal/privacy",   label: "Privacy" },
    { to: "/legal/refunds",   label: "Refunds" },
    { to: "/legal/delivery",  label: "Delivery" },
    { to: "/legal/contact",   label: "Contact" },
];

export default function PublicLayout({ children }) {
    const loc = useLocation();
    const branding = useBranding();
    const logoH = Math.max(24, Math.min(56, Number(branding.logo_display_height) || 32));
    const layout = branding.logo_layout || "icon_and_text";
    const showIcon = layout !== "text_only";
    const showText = layout !== "icon_only";
    const [appName1, ...rest] = (branding.app_name || "Law OS").split(" ");
    const appName2 = rest.join(" ");

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="public-layout">
            <header className="border-b border-border sticky top-0 bg-card/90 backdrop-blur-xl z-10">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="public-brand">
                        {showIcon && (branding.logo_src ? (
                            <img src={branding.logo_src} alt={branding.app_name}
                                style={{ height: logoH, width: "auto", maxWidth: logoH * 2.5 }}
                                className="object-contain"
                                onError={(e) => { e.target.style.display = "none"; }}/>
                        ) : (
                            <div style={{ height: logoH, width: logoH }}
                                className="rounded-lg bg-primary flex items-center justify-center shrink-0">
                                <span className="font-heading text-primary-foreground font-bold"
                                    style={{ fontSize: logoH * 0.45 }}>{(branding.app_name || "L")[0]}</span>
                            </div>
                        ))}
                        {showText && (
                            <div>
                                <div className="font-heading text-[15px] leading-none font-semibold tracking-tight">
                                    {appName1}{appName2 && <span className="text-primary"> {appName2}</span>}
                                </div>
                                {branding.app_tagline && (
                                    <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground mt-0.5">
                                        Legal · Operating · System
                                    </div>
                                )}
                            </div>
                        )}
                    </Link>
                    <nav className="hidden sm:flex items-center gap-1">
                        {NAV.map(n => (
                            <Link key={n.to} to={n.to}
                                data-testid={`public-nav-${n.to.replace(/\W+/g, '-')}`}
                                className={`text-sm px-3 py-2 rounded-lg transition-colors ${
                                    loc.pathname === n.to
                                        ? "bg-primary/10 text-primary font-medium"
                                        : "text-foreground/75 hover:text-primary hover:bg-muted"
                                }`}>
                                {n.label}
                            </Link>
                        ))}
                    </nav>
                    <Link to="/login" data-testid="public-cta-login"
                        className="inline-flex items-center gap-1.5 text-sm bg-primary text-primary-foreground hover:opacity-90 px-4 py-2 rounded-lg font-medium shadow-lawos-sm transition-opacity">
                        Sign in <ArrowRight className="w-3.5 h-3.5"/>
                    </Link>
                </div>
                {/* Mobile nav scroll strip */}
                <nav className="sm:hidden flex items-center gap-3 overflow-x-auto px-4 pb-2 text-xs text-muted-foreground">
                    {NAV.map(n => (
                        <Link key={n.to} to={n.to}
                            className={`whitespace-nowrap px-2 py-1 rounded-md ${loc.pathname === n.to ? "text-primary font-medium" : ""}`}>
                            {n.label}
                        </Link>
                    ))}
                </nav>
            </header>

            <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {children}
            </main>

            <footer className="border-t border-border bg-card/60 mt-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Scale className="w-4 h-4 text-primary" strokeWidth={1.75}/>
                        <span>© {new Date().getFullYear()} Inno Aim Private Limited. All rights reserved.</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Mail className="w-3 h-3" strokeWidth={1.75}/>
                        <a href="mailto:support@lawos.in" className="hover:text-primary">support@lawos.in</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
